<template>
    <td 
        class="editableCell"
        :class="{ 'hasSearch': isEditing,
            'cellFocused': isFocused && !invalid && !isEditing, 
            'cellFocusedInvalid': isFocused && invalid && !isEditing, 
            'cellInvalid': invalid && !isFocused && !isEditing }"
        @click="setFocus"
    >
        <div class="cellWrapper">
            <!-- Editing mode -->
            <div
                v-if="isEditing"
                class="cellContent"
                :class="{ 'cellContentInvalid': invalid, 'cellEditing': !invalid }"
                contenteditable="true"
                ref="inputRef"
                @keydown="handleKeydown"
                @blur="finishEdit"
                @click="handleClick"
            >
                {{ valueFormatted }}
            </div>

            <!-- Display mode -->
            <span 
                v-else 
                class="cellDisplay"
                :class="{ 'nonEditable': !editable }"
                @dblclick="requestEdit"
                @keydown="handleDisplayKeydown"
                tabindex="0"
                ref="displayRef"
            >
                {{ valueFormatted }}
            </span>

            <!-- Search icon -->
            <i v-if="isEditing" class="fas fa-search searchIcon"></i>

            <ModalTablePaginationServer
                :Resource="this.resourceParameter"
                :TypeOption="this.refCode"
                @getCodeConditionOK="codeConditionOkHandler($event)"
                @getCodeConditionCancel="codeConditionCancelHandler($event)"
                :Fields="this.fields"
                :FieldCode="this.fieldCode"
                :FieldDescription="this.fieldDescription"
                :TitleWindow="this.searchTitle"
                :ModalId="this.modalId"
                :AuxFields="this.auxFields"
                :FieldsDate="this.fieldsDate"
                :ShowAddRecord="this.showAddRecord"
                :AddRecordLabel="this.addRecordLabel"
                :valueToSearch="this.valueDescription"
            >
                <template v-if="$slots.body" v-slot:body>
                    <slot name="body" />
                </template>
            </ModalTablePaginationServer>
        </div>
    </td>
</template>

<script>
import { EventBus } from '@/event-bus.js';
import validationMixin from '@/common/validationMixin.js';
import ShowMessage from '@/components/messages/ShowMessage.js';
import Utils from '@/common/utils.js';
import ModalService from '@/services/modal.service'

export default {
    name: 'EditableLookupCell',
    mixins: [validationMixin, ShowMessage],
    props: {
        name: { type: String, required: true },
        value: [String, Number],
        formatter: { type: Function, default: null },
        editable: { type: Boolean, default: true },
        isFocused: { type: Boolean, default: false },
        validatorRules: { type: Array, default: () => [] },

        api: { type: String, required: true },
        fieldCode: { type: String, default: 'code' },
        fieldDescription: { type: String, default: 'description' },
        valueChangedEvent: { type: Function, default: null },
        valueDescription: { type: [String, Number, null], default: null },
        searchTitle: { type: String, required: true },
        searchFieldCode: { type: String, default: 'Code' },
        fields: Array,
        auxFields: Array,
        fieldsDate: Array,
        showAddRecord: { type: Boolean, default: false },
        addRecordLabel: String,
    },
    data() {
        return {
            isEditing: false,
            invalid: false,

            modalId: Utils.generateUUID(),
            fieldsParameter: [],
            resourceParameter: "",
            refCode: Utils.generateUUID(),
            lookupActive: false,
            dirty: false
        };
    },
    computed: {
        valueFormatted() {
            if (this.formatter) {
                return this.formatter(this.value);
            } else if (this.valueDescription) {
                return this.valueDescription;
            }

            return this.value;
        },
        row() {
            const split = this.name.split('-');
            return split.length === 3 ? parseInt(split[1]) : -1;
        }
    },
    methods: {
        setFocus() {
            this.$emit('cellFocus', this.row, this.name);
        },
        requestEdit() {
            if (!this.editable) return;
            this.$emit('requestEdit', { row: this.row, name: this.name });
        },
        startEdit() {
            if (!this.editable) {
                this.$nextTick(() => this.$refs.displayRef.focus());
                return;
            }
            
            this.isEditing = true;
            this.$nextTick(() => {
                const el = this.$refs.inputRef;
                el.focus();

                // Seleccionar todo el contenido
                const range = document.createRange();
                range.selectNodeContents(el);

                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(range);
            });
        },
        async finishEdit(event) {
            if (this.lookupActive === false) {
                const val = event.target.innerText.trim();
                const changed = val !== String(this.valueDescription).trim();

                if (changed) {
                    await this.getLookup(val);
                } else {
                    const valid = this.validateCell(this.value);

                    if (valid) {
                        this.isEditing = false;
                        
                        if (changed) {
                            this.emitChanged(this.value);
                        }
                    } else {
                        this.isEditing = true;
                        //this.$emit('cellFocus', this.row, this.name);
                        //this.$nextTick(() => this.$refs.inputRef.focus());
                        this.showMessage(this.$tc('Msg.Error'), this.invalidMessage(this.name), "errorAutoHide");
                    }
                }
            }
        },

        async handleKeydown(event) { 
            switch (event.key) { 
                case 'Enter': 
                    event.preventDefault(); 
                    await this.finishEdit(event); 

                    if (!this.invalid) this.$emit('nextCell'); 
                    break; 
                case 'Tab': 
                    event.preventDefault(); 
                    await this.finishEdit(event); 

                    if (!this.invalid) { 
                        this.$emit(event.shiftKey ? 'prevCell' : 'nextCell'); 
                    } 
                    break; 
                case 'ArrowUp': 
                    if (this.caretAtStart()) { 
                        event.preventDefault(); 
                        await this.finishEdit(event); 

                        if (!this.invalid) this.$emit('upCell'); 
                    } 
                    break; 
                case 'ArrowDown': 
                    if (this.caretAtEnd()) { 
                        event.preventDefault(); 
                        await this.finishEdit(event); 

                        if (!this.invalid) this.$emit('downCell'); 
                    } 
                    break; 
                case 'ArrowLeft': 
                    if (this.caretAtStart()) { 
                        event.preventDefault(); 
                        await this.finishEdit(event); 

                        if (!this.invalid) this.$emit('prevCell'); 
                    } 
                    break; 
                case 'ArrowRight': 
                    if (this.caretAtEnd()) { 
                        event.preventDefault(); 
                        await this.finishEdit(event); 

                        if (!this.invalid) this.$emit('nextCell'); 
                    } 
                    break; 
            } 
        },

        handleDisplayKeydown(event) {
            switch (event.key) {
                case 'Enter':
                    event.preventDefault();

                    if (this.editable) {
                        this.requestEdit();
                    } else {
                        this.$emit('nextCell'); 
                    }
                    break;

                case 'F2': // opcional, estilo Excel
                    if (this.editable) {
                        event.preventDefault();
                        this.requestEdit();
                    }
                    break;

                case 'Tab':
                    event.preventDefault();
                    this.$emit(event.shiftKey ? 'prevCell' : 'nextCell');
                    break;

                case 'ArrowUp':
                    event.preventDefault();
                    this.$emit('upCell');
                    break;

                case 'ArrowDown':
                    event.preventDefault();
                    this.$emit('downCell');
                    break;

                case 'ArrowLeft':
                    event.preventDefault();
                    this.$emit('prevCell');
                    break;

                case 'ArrowRight':
                    event.preventDefault();
                    this.$emit('nextCell');
                    break;
            }
        },

        caretAtStart() {
            const sel = window.getSelection();
            return sel.anchorOffset === 0;
        },

        caretAtEnd() {
            const sel = window.getSelection();
            const el = this.$refs.inputRef;
            return sel.anchorOffset === el.innerText.length;
        },

        handleClick(event) {
            const rect = this.$el.getBoundingClientRect();
            
            if (event.clientX - rect.left > rect.width - 35) {
                this.$nextTick(() => this.callModalTable());
            }
        },

        callModalTable() {
            this.lookupActive = true;
            this.fieldsParameter = [
                this.fieldCode,
                this.fieldDescription
            ];
            this.resourceParameter = "/" + this.api;
            this.$bvModal.show(this.modalId);
        },

        codeConditionOkHandler(result) {
            this.lookupActive = false;
            var type = result.FieldName;
            if (type.indexOf(this.refCode) !== -1 && result.code) {
                this.$emit('input', result.code);
                this.$emit('update:valueDescription', result.description);
                this.emitChanged(result.code);
                
                if (this.valueChangedEvent) {
                    this.valueChangedEvent(result);
                }

                this.dirty = false;
            } else {
                this.$emit('input', '');
                this.$emit('update:valueDescription', '');
            }
            
            this.resourceParameter = "";
            this.$bvModal.hide(this.modalId);
        },

        codeConditionCancelHandler(){
            this.lookupActive = false;
            this.resourceParameter = "";
        },

        async getLookup(value) {
            var result = {};

            if (Utils.isNullOrEmptyString(value) === false) {
                let parameter = {};

                if (this.searchFieldCode) {
                    parameter[this.searchFieldCode] = value;
                }
                else {
                    parameter.SearchCriteria = value;
                }
                
                await ModalService.getAll("/" + this.api, parameter)
                    .then((response) => {
                        if (response.status == '200' && response.data && response.data.length > 0) {
                            result.FieldName = this.refCode;
                            result.code = response.data[0][this.fieldCode];
                            result.description = response.data[0][this.fieldDescription];
                            
                            if (this.auxFields) {
                                result.AuxFields = [];
                                this.auxFields.forEach(element => {
                                    result.AuxFields.push(response.data[0][element])
                                });
                            }

                            const valid = this.validateCell(result.code);
                            if (valid) {
                                this.$emit('input', result.code);
                                this.$emit('update:valueDescription', result.description);
                                this.emitChanged(result.code);

                                if (this.valueChangedEvent) {
                                    this.valueChangedEvent(result);
                                }
                                
                                this.isEditing = false;
                            } else {
                                this.isEditing = true;
                                //this.$emit('cellFocus', this.row, this.name);
                                //this.$nextTick(() => this.$refs.inputRef.focus());
                                this.showMessage(this.$tc('Msg.Error'), this.invalidMessage(this.name), "errorAutoHide");
                            }
                        } else {
                            this.valid = false;
                            this.isEditing = true;
                            //this.$emit('cellFocus', this.row, this.name);
                            //this.$nextTick(() => this.$refs.inputRef.focus());
                            this.showMessage(this.$tc('Msg.Error'), this.$t('eItemsReceivedEdit.NotFoundMsg'), "errorAutoHide");
                        }
                    })
                    .catch(error => {
                        this.$emit('input', '');
                        this.emitChanged('');

                        this.valid = false;
                        this.isEditing = true;
                        //this.$emit('cellFocus', this.row, this.name);
                        //this.$nextTick(() => this.$refs.inputRef.focus());
                        this.showResponseErrorMessage(error);
                    });
            } 
            else {
                const valid = this.validateCell('');

                if (valid) {
                    this.$emit('input', '');
                    this.$emit('update:valueDescription', '');
                    this.emitChanged('');

                    result.FieldName = this.refCode;
                    result.code = '';
                    result.description = '';
                    
                    if (this.valueChangedEvent) {
                        this.valueChangedEvent(result);
                    }
                    this.isEditing = false;
                } else {
                    this.isEditing = true;
                    //this.$emit('cellFocus', this.row, this.name);
                    //this.$nextTick(() => this.$refs.inputRef.focus());
                    this.showMessage(this.$tc('Msg.Error'), this.invalidMessage(this.name), "errorAutoHide");
                }
            }
        },

        emitChanged(value) {
            EventBus.$emit('onChanged', { Changed: true, componentName: this.name, value });
        },
        validateCell(val = null) {
            let state = true;
            const valueToCheck = val !== null ? val : String(this.value ?? '').trim();

            if (this.validatorRules) {
                const rules = {
                [this.name]: this.validatorRules
                };
                state = this.validateFields(rules, { [this.name]: valueToCheck });
            }

            this.invalid = !state;
            if (this.isFocused) {
                this.$emit('validationState', { name: this.name, invalid: this.invalid });
            }

            return state;
        },
    }
};
</script>

<style scoped>
.editableCell { padding: 0 !important; min-width: 120px; }
.cellFocused { box-shadow: inset 0 0 0 1px #007bff !important; }
.cellWrapper { position: relative; display: flex; align-items: center; width: 100%; height: 100%; }
.cellDisplay { flex: 1; padding: 8px; cursor: pointer; }
.nonEditable { cursor: default; color: #555; }
.cellEditing { flex: 1; padding: 6px; outline: none; width: calc(100% - 6px); margin: 2px;
    border: 2px solid #007bff; border-radius: 4px; background-color: #fff; 
    cursor: text;}
.hasSearch .cellContent, .hasSearch .cellDisplay { padding-right: 30px; }
.searchIcon { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); color: #007bff; pointer-events: none; }
.searchIcon:hover { cursor: pointer; }
.cellContentInvalid { flex: 1; padding: 6px; outline: none; width: calc(100% - 6px); margin: 2px;
    border: 2px solid red; border-radius: 4px; background-color: #fff; }
.cellInvalid { background-color: #fae3de !important; box-shadow: inset 0 0 0 1px red !important; }
.cellFocusedInvalid { background-color: #fae3de !important; box-shadow: inset 0 0 0 1px #007bff !important; }
</style>
